import { builder } from '@builder.io/react';
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
  NextPage,
} from 'next';
import { useTranslation } from 'next-i18next/pages';
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations';
import { useRouter } from 'next/router';

import { InternalLocale } from '@catawiki/locale';
import {
  getRuntimeConfigPageProps,
  RuntimeConfigPageProps,
} from '@catawiki/next-config/runtime-config';

import { getI18n } from '@/shared/utils/getI18n';
import {
  LayoutAssets,
  fetchLayoutAssets,
} from '@/shared/components/LayoutData';
import { resolveBuilderContent } from '@/shared/builderio';
import { SharedPage } from '@/shared/components/SharedPage';
import { captureException } from '@/shared/utils/captureException';

import config from '../../../config';

const MODEL_NAME = 'page';
export const PAGE_PATH_PREFIX = '/en/pages/p/';

builder.init(config.builderApiKey);

type Props = InferGetStaticPropsType<typeof getStaticProps>;
type PageStaticProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  page: any;
  locale: string;
  assetsData: LayoutAssets;
};

const Page: NextPage<Props> = ({ page, locale }) => {
  const router = useRouter();
  const { t } = useTranslation();
  builder.setUserAttributes({ locale });

  const { title, description, thumbnail, type, robots } = page?.data || {
    title: `${t('seo.not_found')} - Catawiki`,
  };
  return (
    <SharedPage
      title={title}
      description={description ?? undefined}
      asPath={router.asPath}
      locale={locale}
      type={type}
      robots={robots}
      modelName={MODEL_NAME}
      page={page}
      thumbnail={thumbnail}
    />
  );
};

export default Page;

export const getStaticProps: GetStaticProps<
  PageStaticProps & RuntimeConfigPageProps,
  { page: string }
> = async ({ params, locale = 'en' }) => {
  try {
    const urlPath = params?.page || '';

    const i18n = getI18n(locale as InternalLocale);
    const [page, translations] = await Promise.all([
      resolveBuilderContent(
        MODEL_NAME,
        locale,
        PAGE_PATH_PREFIX,
        urlPath as string,
        i18n
      ),
      serverSideTranslations(locale),
    ]);

    const assetsData = await fetchLayoutAssets(
      locale as InternalLocale,
      page?.data?.theme
    );
    const revalidateTime = 300;

    return {
      props: {
        ...getRuntimeConfigPageProps(),
        // Log revlidate time
        revalidateTime,
        // assetsData will be passed to Layout component in _app
        assetsData,
        // NextJS won't clean up undefined and they don't want to change this https://github.com/vercel/next.js/discussions/11209
        // but data from Builder.io can be undefined, so this is to make sure that NextJS page is still work even the
        // Builder data is invalid
        page: JSON.parse(JSON.stringify(page)),
        theme: page?.data?.theme || '',
        partnershipLogo: page?.data?.partnershipLogo || '',
        stickyHeader: page?.data?.stickyHeader || false,
        locale,
        ...translations,
      },
      notFound: !page,
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 30 seconds
      revalidate: revalidateTime,
    };
  } catch (error) {
    captureException(error as Error, { extra: { params } });
    return {
      revalidate: 1,
      notFound: true,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking',
});
